import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import MaskedInput from "react-text-mask";
import * as Yup from "yup";
import AssignmentIndOutlinedIcon from "@material-ui/icons/AssignmentIndOutlined";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { baseUrl, config } from "../../../services/config";

const cnicNumberMask = [
  /[1-9]/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  "-",
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  /\d/,
  "-",
  /\d/,
];

const validationSchema = Yup.object().shape({
  cnic: Yup.string()
    .matches(/^\d{5}-\d{7}-\d$/, "CNIC must follow the XXXXX-XXXXXXX-X format")
    .required("CNIC is required"),
  password: Yup.string()
    .min(3, "Minimum 3 characters")
    .max(50, "Maximum 50 characters")
    .required("Password is required"),
});

export default function ParentLogin() {
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const [schoolLogo, setSchoolLogo] = useState("");
  const [unitName, setUnitName] = useState("");

  useEffect(() => {
    fetch(`${baseUrl}/get-settings`, { method: "GET" })
      .then((res) => res.json())
      .then((data) => {
        setUnitName(data?.payload?.settings?.unit_name || "");
        setSchoolLogo(data?.payload?.settings?.start_logo_file || "");
      })
      .catch((error) => console.log("Parent login settings error:", error));
  }, []);

  const headerBackground = `linear-gradient(160deg, ${config?.sideBarBgColor ||
    "#1b1b28"} 0%, ${config?.sideBarColor || "#121291"} 100%)`;

  const formik = useFormik({
    initialValues: { cnic: "", password: "" },
    validationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setStatus("");
      try {
        const response = await fetch(`${baseUrl}/login`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            cnic: values.cnic,
            password: values.password,
          }),
        });
        const result = await response.json();

        if (response.ok && result.payload) {
          const { token, user, children } = result.payload;
          localStorage.setItem("parentToken", token);
          localStorage.setItem("parentUser", JSON.stringify(user));
          localStorage.setItem(
            "parentChildren",
            JSON.stringify(children || []),
          );
          history.push("/parent/dashboard");
          return;
        }
        setStatus(result.message || "Invalid CNIC or password.");
      } catch (error) {
        console.error("Parent login error:", error);
        setStatus("Network error. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const inputStyle = (fieldName) => ({
    width: "100%",
    border: `1.5px solid ${
      formik.touched[fieldName] && formik.errors[fieldName]
        ? "#e63946"
        : formik.touched[fieldName]
        ? "#27ae60"
        : "#e2e8f0"
    }`,
    borderRadius: 10,
    padding: "13px 44px",
    fontSize: 15,
    color: "#0f172a",
    background: "#f8fafc",
    outline: "none",
    boxSizing: "border-box",
  });

  const iconStyle = {
    position: "absolute",
    left: 14,
    top: "50%",
    transform: "translateY(-50%)",
    color: "#94a3b8",
    fontSize: 20,
    pointerEvents: "none",
  };

  const errorFor = (fieldName) =>
    formik.touched[fieldName] && formik.errors[fieldName] ? (
      <p
        style={{
          color: "#e63946",
          fontSize: 12,
          fontWeight: 600,
          margin: "5px 0 0",
        }}
      >
        {formik.errors[fieldName]}
      </p>
    ) : null;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f1f5f9",
      }}
    >
      <div
        style={{
          width: 420,
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 20px 60px rgba(15,23,42,0.12)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: headerBackground,
            padding: "32px 40px 28px",
            color: "#fff",
            textAlign: "center",
          }}
        >
          {/* School logo */}
          {schoolLogo ? (
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#fff",
                borderRadius: 12,
                padding: "10px 16px",
                marginBottom: 16,
                boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
              }}
            >
              <img
                src={schoolLogo}
                alt="School Logo"
                style={{
                  maxHeight: 56,
                  maxWidth: 180,
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </div>
          ) : (
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <LockOutlinedIcon style={{ fontSize: 28, color: "#fff" }} />
            </div>
          )}

          <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700 }}>
            {unitName || "Parent Portal"}
          </h2>
          <p style={{ margin: "6px 0 0", fontSize: 13, opacity: 0.75 }}>
            {unitName
              ? "Parent Portal — sign in with your CNIC"
              : "Sign in with your CNIC to continue"}
          </p>
        </div>

        <form
          onSubmit={formik.handleSubmit}
          noValidate
          style={{ padding: "32px 40px 36px" }}
        >
          {formik.status && (
            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                padding: "10px 14px",
                color: "#dc2626",
                fontSize: 13,
                marginBottom: 20,
              }}
            >
              {formik.status}
            </div>
          )}

          <div style={{ marginBottom: 18 }}>
            <label
              htmlFor="cnic"
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 6,
              }}
            >
              CNIC
            </label>
            <div style={{ position: "relative" }}>
              <AssignmentIndOutlinedIcon style={iconStyle} />
              <MaskedInput
                {...formik.getFieldProps("cnic")}
                id="cnic"
                mask={cnicNumberMask}
                guide={false}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="35201-1234567-1"
                style={inputStyle("cnic")}
              />
            </div>
            {errorFor("cnic")}
          </div>

          <div style={{ marginBottom: 24 }}>
            <label
              htmlFor="password"
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 6,
              }}
            >
              Password
            </label>
            <div style={{ position: "relative" }}>
              <LockOutlinedIcon style={iconStyle} />
              <input
                {...formik.getFieldProps("password")}
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                placeholder="Enter password"
                style={{ ...inputStyle("password"), paddingRight: 48 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                style={{
                  position: "absolute",
                  right: 14,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  padding: 0,
                  border: "none",
                  background: "none",
                  color: "#94a3b8",
                  cursor: "pointer",
                }}
              >
                {showPassword ? (
                  <VisibilityOff style={{ fontSize: 20 }} />
                ) : (
                  <Visibility style={{ fontSize: 20 }} />
                )}
              </button>
            </div>
            {errorFor("password")}
          </div>

          <button
            type="submit"
            disabled={formik.isSubmitting}
            style={{
              width: "100%",
              background: formik.isSubmitting
                ? "#9ca3af"
                : `${config?.btnColor}`,
              color: "#fff",
              border: "none",
              borderRadius: 10,
              padding: 14,
              fontSize: 15,
              fontWeight: 700,
              cursor: formik.isSubmitting ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            {formik.isSubmitting && (
              <span
                className="spinner-border spinner-border-sm"
                style={{ width: 16, height: 16 }}
              />
            )}
            {formik.isSubmitting ? "Signing in..." : "Sign In"}
          </button>

          <div
            style={{
              marginTop: 24,
              textAlign: "center",
              fontSize: 12,
              color: "#94a3b8",
            }}
          >
            Powered by{" "}
            <a
              href="https://tvoxel.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-color-primary"
              style={{
                color: config?.btnColor || "#0f766e",
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Tvoxel Technologies
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
